import { T } from '@tolgee/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';

import EmojiThanks from '../../../assets/icons/EmojiThanks';
import theme from '../../../assets/theme';
import { LicenseCoarseLevels } from '../../../hooks/useAccessBadge';
import { useMetrics } from '../../../hooks/useMetrics';
import { getTrackingParams } from '../../../utils/adsConversionTrackingUtils';
import { Button } from '../../_atoms/Button';
import BaseModal from '../../_molecules/BaseModal';
import { planIdsFor } from './planData';
import {
  MatrixOverviewRow,
  matrixOverviewRows,
  MatrixPlanColumn,
  MatrixPlanCta as MatrixPlanCtaConfig,
  MatrixPlanCtaVariant,
  staticMatrixPlanColumns,
} from './planOverviewMatrixData';
import {
  CONTENT_MAX_WIDTH,
  CREAM_BG,
  HAIRLINE_WARM,
  MATRIX_CARD_BORDER,
  MATRIX_FREE_ACCENT,
  MATRIX_GREEN_BG,
  MATRIX_INK,
  MATRIX_MUTED,
  MATRIX_ROW_LABEL,
  MOBILE_QUERY,
  MUTED_BODY,
  NARROW_QUERY,
} from './pricingTokens';
import { useIndividualPremiumCheckout } from './useIndividualPremiumCheckout';

export const QUOTE_REQUEST_HREF = '/quotes?step=form';
export const BOOK_DEMO_HREF = '/quotes';

type PlanOverviewSectionProps = {
  showIndividualPremium: boolean;
  isUSPricing?: boolean;
};

export const PlanOverviewSection = ({
  showIndividualPremium,
  isUSPricing,
}: PlanOverviewSectionProps) => {
  const metrics = useMetrics(true);
  const router = useRouter();
  const {
    isAuthenticated,
    userAccessBadge,
    billingFrequency,
    setBillingFrequency,
    isCheckoutLoading,
    showAuthGuard,
    handleIndividualCheckout,
    handleAuthGuardSignIn,
    handleAuthGuardSignUp,
  } = useIndividualPremiumCheckout();

  const planIds = planIdsFor(showIndividualPremium);

  const handleSignup = () => {
    metrics.logEvent('PricingPage.GetStartedFree.clicked');
    const trackingParams = getTrackingParams();
    const query: Record<string, string> = { callback: '/' };
    if (trackingParams.fbclid) query.fbclid = trackingParams.fbclid;
    if (trackingParams.ttclid) query.ttclid = trackingParams.ttclid;
    router.push({ pathname: '/signup', query });
  };

  const isFreemium =
    userAccessBadge?.license?.coarseLevel === LicenseCoarseLevels.freemium;
  // Not just "authenticated and not freemium" — that also matched Enterprise
  // licence holders, marking School & District customers as "already on
  // Premium" too.
  const isOnPaidIndividualPlan =
    userAccessBadge?.license?.coarseLevel === LicenseCoarseLevels.premium;
  const isEnterprise =
    userAccessBadge?.license?.coarseLevel === LicenseCoarseLevels.enterprise;

  const currentPlanNote = (
    <CurrentPlanNote>
      <T keyName="Your current plan" defaultValue="Your current plan" />
    </CurrentPlanNote>
  );

  const columns: MatrixPlanColumn[] = planIds.map(id => {
    const base = staticMatrixPlanColumns[id];
    switch (id) {
      case 'free':
        // Signed-in teachers are already on the free plan, so don't push them
        // back into signup; anyone on a paid licence gets no Free CTA at all.
        return {
          ...base,
          price: (
            <PriceValue>
              <T keyName="Pricing.Plan.Free.Price" defaultValue="$0" />
              <PriceUnit>
                <T keyName="Pricing.Plan.Free.PriceUnit" defaultValue="/month" />
              </PriceUnit>
            </PriceValue>
          ),
          cta: isAuthenticated
            ? undefined
            : {
                label: (
                  <T keyName="Pricing.Matrix.Free.Cta" defaultValue="Get started free" />
                ),
                onClick: handleSignup,
                variant: 'solidDark',
              },
          ctaReplacement: isAuthenticated && isFreemium ? currentPlanNote : undefined,
        };
      case 'premium':
        return {
          ...base,
          price: (
            <PremiumPrice
              billingFrequency={billingFrequency}
              setBillingFrequency={setBillingFrequency}
              isUSPricing={isUSPricing}
            />
          ),
          cta: {
            label: <T keyName="Upgrade to Premium" defaultValue="Upgrade to Premium" />,
            onClick: handleIndividualCheckout,
            variant: 'outlineDark',
          },
          ctaReplacement: isOnPaidIndividualPlan ? currentPlanNote : undefined,
        };
      case 'schoolDistrict':
        // A school or district that's already bought a licence shouldn't be
        // asked to request another quote.
        return {
          ...base,
          price: (
            <PriceCaption>
              <T keyName="Pricing.Plan.School.Price" defaultValue="Custom pricing" />
            </PriceCaption>
          ),
          cta: {
            label: (
              <T keyName="Pricing.Matrix.School.Cta" defaultValue="Request a quote" />
            ),
            href: QUOTE_REQUEST_HREF,
            onClick: () => metrics.logEvent('PricingPage.RequestQuote.clicked'),
            variant: 'solidDark',
          },
          ctaReplacement: isEnterprise ? currentPlanNote : undefined,
        };
      case 'curriculumAlignment':
      default:
        return {
          ...base,
          price: (
            <PriceCaption>
              <T keyName="Pricing.Plan.Curriculum.Price" defaultValue="Custom pricing" />
            </PriceCaption>
          ),
          cta: {
            label: (
              <T keyName="Pricing.Matrix.Curriculum.Cta" defaultValue="Book a demo" />
            ),
            href: BOOK_DEMO_HREF,
            onClick: () => metrics.logEvent('PricingPage.BookDemo.clicked'),
            variant: 'solidWhite',
          },
        };
    }
  });

  return (
    <Section id="plans">
      <BaseModal hideCancel open={showAuthGuard} width="500px" centered>
        <ModalContent>
          <IconContainer>
            <span style={{ color: '#666' }}>
              <EmojiThanks />
            </span>
          </IconContainer>
          <ModalTitle>
            <T
              keyName="You need to be logged in to upgrade"
              defaultValue="You need to be logged in to upgrade"
            />
          </ModalTitle>
          <ModalMessage>
            <T
              keyName="Please log in or create an account"
              defaultValue="Please log in or create an account"
            />
          </ModalMessage>
          <ModalButtonContainer>
            <Button
              type="ghost"
              onClick={handleAuthGuardSignIn}
              styles={{ marginRight: '12px' }}
            >
              <T keyName="Log in" defaultValue="Log in" />
            </Button>
            <Button
              type="primary"
              onClick={handleAuthGuardSignUp}
              styles={{ marginRight: '12px' }}
            >
              <T keyName="Sign up" defaultValue="Sign up" />
            </Button>
          </ModalButtonContainer>
        </ModalContent>
      </BaseModal>

      <Inner>
        <MatrixCard>
          <Intro>
            <SectionHeading>
              <T
                keyName="Pricing.Overview.Heading"
                defaultValue="What you get on each plan"
              />
            </SectionHeading>
            <IntroSub>
              <T
                keyName="Pricing.Overview.Sub.Lead"
                defaultValue="The highlights at a glance."
              />{' '}
              <T keyName="Pricing.Overview.Sub.The" defaultValue="The" />{' '}
              <a href="#compare">
                <T
                  keyName="Pricing.Overview.Sub.Link"
                  defaultValue="full feature comparison"
                />
              </a>{' '}
              <T keyName="Pricing.Overview.Sub.End" defaultValue="is further down." />
            </IntroSub>
          </Intro>

          <DesktopGrid style={{ '--plan-count': columns.length } as CSSProperties}>
            <LabelEyebrowCell>
              <T keyName="Pricing.Overview.ComparePlans" defaultValue="Compare plans" />
            </LabelEyebrowCell>
            {columns.map(column => (
              <IdentityCell
                key={column.id}
                $bg={column.headerBg}
                data-testid={`desktop-identity-${column.id}`}
              >
                {column.badge && <Badge>{column.badge}</Badge>}
                <PlanTitle $fg={column.headerFg}>{column.title}</PlanTitle>
                <PlanAudience $fg={column.audienceFg}>{column.audience}</PlanAudience>
              </IdentityCell>
            ))}

            <LabelSpacerCell />
            {columns.map(column => (
              <PriceCell
                key={column.id}
                $bg={column.headerBg}
                $fg={column.headerFg}
                data-testid={`desktop-price-${column.id}`}
              >
                {column.price}
              </PriceCell>
            ))}

            <LabelSpacerCell />
            {columns.map(column => (
              <ActionCell
                key={column.id}
                $bg={column.headerBg}
                $fg={column.headerFg}
                data-testid={`desktop-action-${column.id}`}
              >
                {column.ctaReplacement ??
                  (column.cta ? (
                    <PlanCta
                      cta={column.cta}
                      isLoading={isCheckoutLoading}
                      compact={false}
                    />
                  ) : null)}
              </ActionCell>
            ))}

            <LabelRowCell>
              <T keyName="Pricing.Overview.ForTeachers" defaultValue="For Teachers" />
            </LabelRowCell>
            {matrixOverviewRows[0] &&
              columns.map(column => (
                <BodyCell key={column.id}>
                  {matrixOverviewRows[0].cells[column.id] ?? <Dash>—</Dash>}
                </BodyCell>
              ))}

            <LabelRowCell $withTopBorder>
              <T
                keyName="Pricing.Overview.ForLeaders"
                defaultValue="For ELA Curriculum & Instruction Leaders"
              />
            </LabelRowCell>
            {matrixOverviewRows[1] &&
              columns.map(column => (
                <BodyCell key={column.id} $withTopBorder>
                  {matrixOverviewRows[1].cells[column.id] ?? <Dash>—</Dash>}
                </BodyCell>
              ))}
          </DesktopGrid>

          <MobileStack>
            {columns.map(column => (
              <MobilePlanCard
                key={column.id}
                column={column}
                rows={matrixOverviewRows}
                isCheckoutLoading={isCheckoutLoading}
              />
            ))}
          </MobileStack>
        </MatrixCard>
      </Inner>
    </Section>
  );
};

const MobilePlanCard = ({
  column,
  rows,
  isCheckoutLoading,
}: {
  column: MatrixPlanColumn;
  rows: MatrixOverviewRow[];
  isCheckoutLoading: boolean;
}) => {
  // A plan with nothing to say for a row omits that block entirely on mobile
  // (matches the mock — Free's card has no second row at all) rather than
  // showing an empty heading over a dash, which reads oddly stacked.
  const visibleRows = rows.filter(row => row.cells[column.id]);

  return (
    <MobileCard data-testid={`mobile-card-${column.id}`}>
      {column.badge && <Badge>{column.badge}</Badge>}
      <MobileCardHeader
        $bg={column.headerBg}
        $fg={column.headerFg}
        $hasBadge={!!column.badge}
      >
        <MobileTitle $fg={column.headerFg}>{column.title}</MobileTitle>
        <MobileAudience $fg={column.audienceFg}>{column.audience}</MobileAudience>
        <MobilePriceSlot>{column.price}</MobilePriceSlot>
        <MobileCtaWrapper>
          {column.ctaReplacement ??
            (column.cta ? (
              <PlanCta cta={column.cta} isLoading={isCheckoutLoading} compact />
            ) : null)}
        </MobileCtaWrapper>
      </MobileCardHeader>
      {visibleRows.length > 0 && (
        <MobileBody>
          {visibleRows.map((row, index) => (
            <MobileRowBlock
              key={row.id}
              $withBottomBorder={index !== visibleRows.length - 1}
            >
              <MobileRowLabel>{row.label}</MobileRowLabel>
              <MobileRowText>{row.cells[column.id]}</MobileRowText>
            </MobileRowBlock>
          ))}
        </MobileBody>
      )}
    </MobileCard>
  );
};

const PlanCta = ({
  cta,
  isLoading,
  compact,
}: {
  cta: MatrixPlanCtaConfig;
  isLoading: boolean;
  compact: boolean;
}) => {
  if (!cta.href) {
    return (
      <CtaButton
        type="button"
        $variant={cta.variant}
        $compact={compact}
        disabled={isLoading}
        aria-busy={isLoading}
        onClick={cta.onClick}
      >
        {/* Stripe's createCheckoutSession round-trip takes real network time
            before the redirect fires — without this the click looks inert. */}
        {isLoading && <ButtonSpinner aria-hidden="true" data-testid="cta-spinner" />}
        {cta.label}
      </CtaButton>
    );
  }

  // `passHref` is required: next/link only forwards href to a literal <a>, and
  // CtaLink is a styled component.
  return (
    <Link href={cta.href} passHref>
      <CtaLink $variant={cta.variant} $compact={compact} onClick={cta.onClick}>
        {cta.label}
      </CtaLink>
    </Link>
  );
};

const PremiumPrice = ({
  billingFrequency,
  setBillingFrequency,
  isUSPricing,
}: {
  billingFrequency: 'monthly' | 'yearly';
  setBillingFrequency: (value: 'monthly' | 'yearly') => void;
  isUSPricing?: boolean;
}) => (
  <PremiumPriceWrapper>
    <BillingToggle>
      <BillingOption
        type="button"
        $isActive={billingFrequency === 'monthly'}
        onClick={() => setBillingFrequency('monthly')}
      >
        <T keyName="Monthly" defaultValue="Monthly" />
      </BillingOption>
      <BillingOption
        type="button"
        $isActive={billingFrequency === 'yearly'}
        onClick={() => setBillingFrequency('yearly')}
      >
        <T keyName="Yearly" defaultValue="Yearly" />
        <SaveBadge>
          {isUSPricing ? (
            <T keyName="Save 20%" defaultValue="Save 20%" />
          ) : (
            <T keyName="Save 21%" defaultValue="Save 21%" />
          )}
        </SaveBadge>
      </BillingOption>
    </BillingToggle>
    <PremiumPriceValue>
      {isUSPricing ? (
        billingFrequency === 'yearly' ? (
          <T keyName="pricing.us.yearly" defaultValue="$240/year" />
        ) : (
          <T keyName="pricing.us.monthly" defaultValue="$25/month" />
        )
      ) : billingFrequency === 'yearly' ? (
        <T keyName="pricing.intl.yearly" defaultValue="$228/year" />
      ) : (
        <T keyName="$24/month" defaultValue="$24/month" />
      )}
    </PremiumPriceValue>
    {billingFrequency === 'yearly' && (
      <YearlySavingsText>
        <T keyName="pricing.yearly.savings" defaultValue="save $60 vs monthly plan" />
      </YearlySavingsText>
    )}
  </PremiumPriceWrapper>
);

/* ─── Layout ──────────────────────────────────────────────────────────────── */

const Section = styled.section`
  width: 100%;
  background: ${CREAM_BG};
  padding: 84px 32px 20px;

  @media ${MOBILE_QUERY} {
    padding: 56px 20px 16px;
  }
`;

const Inner = styled.div`
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
`;

const MatrixCard = styled.div`
  background: ${theme.colors.landingPageBeige};
  border: 1px solid ${MATRIX_CARD_BORDER};
  border-radius: 20px;
  padding: 64px 72px 72px;

  @media ${MOBILE_QUERY} {
    padding: 40px 28px 44px;
  }

  @media ${NARROW_QUERY} {
    padding: 32px 20px 40px;
  }
`;

const Intro = styled.div`
  text-align: center;
  margin-bottom: 44px;

  @media ${MOBILE_QUERY} {
    text-align: left;
    margin-bottom: 24px;
  }
`;

const SectionHeading = styled.h2`
  margin: 0;
  font-size: 40px;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: ${MATRIX_INK};

  @media ${MOBILE_QUERY} {
    font-size: 28px;
    line-height: 1.15;
  }
`;

const IntroSub = styled.p`
  && {
    margin: 12px 0 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.5;
    color: ${MATRIX_MUTED};
  }

  a {
    font-weight: 700;
    color: ${MATRIX_INK};
    border-bottom: 1.5px solid ${theme.colors.orange};
  }

  @media ${MOBILE_QUERY} {
    && {
      font-size: 15px;
      line-height: 1.55;
    }
  }
`;

/* ─── Desktop matrix grid ──────────────────────────────────────────────────── */

const DesktopGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr repeat(var(--plan-count), 1fr);
  background: ${theme.colors.white};
  border: 1px solid ${MATRIX_CARD_BORDER};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 20px 0 #deaf8d33;

  @media ${MOBILE_QUERY} {
    display: none;
  }
`;

/**
 * CSS Grid's fractional (`fr`) column widths don't always sum to an exact
 * whole pixel — the leftover fraction isn't absorbed by any track, so it
 * shows up as a hairline sliver of the grid's own white background at
 * whatever boundary the rounding favours (which boundary varies with the
 * viewport width and the column count, so this isn't limited to the grid's
 * outer edges). Overlapping every cell into its neighbours by 1px on each
 * side hides the gap wherever it lands, without needing to special-case a
 * particular column.
 */
const overlapNeighbours = `
  margin-left: -1px;
  margin-right: -1px;
`;

const LabelEyebrowCell = styled.div`
  ${overlapNeighbours}
  padding: 40px 28px 16px;
  display: flex;
  align-items: flex-end;
  background: ${theme.colors.paleBeige};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${MATRIX_MUTED};
`;

const LabelSpacerCell = styled.div`
  ${overlapNeighbours}
  background: ${theme.colors.paleBeige};
`;

const LabelRowCell = styled.div<{ $withTopBorder?: boolean }>`
  ${overlapNeighbours}
  padding: 28px;
  background: ${theme.colors.paleBeige};
  font-size: 15px;
  font-weight: 900;
  line-height: 1.5;
  color: ${MATRIX_INK};
  ${({ $withTopBorder }) =>
    $withTopBorder && `border-top: 1px solid ${MATRIX_CARD_BORDER};`}
`;

const IdentityCell = styled.div<{ $bg: string }>`
  ${overlapNeighbours}
  position: relative;
  padding: 40px 26px 16px;
  background: ${({ $bg }) => $bg};
`;

/**
 * Curriculum Alignment's "Recommended" badge, on both the desktop grid and
 * the mobile stacked cards. Sits inside the coloured header rather than
 * straddling its top edge — the grid and the mobile card both clip their
 * first child with `overflow: hidden` (to keep its square corners inside
 * their own border-radius), which would cut off a badge overlapping that
 * border.
 */
const Badge = styled.span`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: ${MATRIX_INK};
  color: ${theme.colors.landingPageBeige};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(40, 47, 51, 0.2);
`;

const PlanTitle = styled.div<{ $fg: string }>`
  font-size: 24px;
  font-weight: 900;
  line-height: 1.2;
  color: ${({ $fg }) => $fg};
`;

const PlanAudience = styled.div<{ $fg: string }>`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $fg }) => $fg};
  margin-top: 6px;
`;

const PriceCell = styled.div<{ $bg: string; $fg: string }>`
  ${overlapNeighbours}
  padding: 22px 26px 0;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const PriceValue = styled.div`
  font-size: 28px;
  font-weight: 900;
  line-height: 1.1;
`;

const PriceUnit = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${MATRIX_FREE_ACCENT};
`;

const PriceCaption = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const ActionCell = styled.div<{ $bg: string; $fg: string }>`
  ${overlapNeighbours}
  padding: 22px 26px 34px;
  background: ${({ $bg }) => $bg};
  /* CurrentPlanNote has no colour of its own — without this it would inherit
     a fixed dark default regardless of column, unreadable against School &
     District's dark green background. */
  color: ${({ $fg }) => $fg};
`;

const BodyCell = styled.div<{ $withTopBorder?: boolean }>`
  padding: 28px 26px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  color: ${MUTED_BODY};
  ${({ $withTopBorder }) =>
    $withTopBorder && `border-top: 1px solid ${MATRIX_CARD_BORDER};`}

  a {
    font-weight: 700;
  }
`;

const Dash = styled.span`
  font-size: 18px;
  color: #c9c1b6;
`;

/* ─── CTA pill (shared by the desktop grid and mobile cards) ──────────────── */

const ctaVariants = {
  solidDark: `
    background: ${MATRIX_INK};
    color: ${theme.colors.landingPageBeige};
    &:hover { background: #000000; color: ${theme.colors.white}; }
  `,
  solidWhite: `
    background: ${theme.colors.white};
    color: ${MATRIX_GREEN_BG};
    &:hover { background: ${MATRIX_INK}; color: ${theme.colors.white}; }
  `,
  outlineDark: `
    border: 1.5px solid ${MATRIX_INK};
    color: ${MATRIX_INK};
    background: transparent;
    &:hover { background: ${MATRIX_INK}; color: ${theme.colors.landingPageBeige}; }
  `,
};

const ctaBase = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: none;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 900;
  letter-spacing: 0.01em;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 2px 10px rgba(40, 47, 51, 0.16);
  transition: all 0.2s ease;
`;

const CtaLink = styled.a<{ $variant: MatrixPlanCtaVariant; $compact: boolean }>`
  ${ctaBase}
  ${({ $variant }) => ctaVariants[$variant]}
  height: ${({ $compact }) => ($compact ? '50px' : '56px')};
  font-size: ${({ $compact }) => ($compact ? '15px' : '16px')};
`;

const CtaButton = styled.button<{ $variant: MatrixPlanCtaVariant; $compact: boolean }>`
  ${ctaBase}
  ${({ $variant }) => ctaVariants[$variant]}
  height: ${({ $compact }) => ($compact ? '50px' : '56px')};
  font-size: ${({ $compact }) => ($compact ? '15px' : '16px')};
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    /* Belt and braces alongside the disabled attribute: guarantees a second
       click can't fire a second checkout session while the first is pending. */
    pointer-events: none;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const ButtonSpinner = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: ${spin} 0.6s linear infinite;
`;

const CurrentPlanNote = styled.p`
  && {
    margin: 0;
    text-align: center;
    font-size: 14px;
    font-weight: 800;
    color: inherit;
  }
`;

/* ─── Mobile stacked cards (design variant 4e) ────────────────────────────── */

const MobileStack = styled.div`
  display: none;

  @media ${MOBILE_QUERY} {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const MobileCard = styled.div`
  position: relative;
  background: ${theme.colors.white};
  border: 1px solid ${MATRIX_CARD_BORDER};
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 20px 0 #deaf8d33;
`;

const MobileCardHeader = styled.div<{ $bg: string; $fg: string; $hasBadge?: boolean }>`
  padding: ${({ $hasBadge }) => ($hasBadge ? '40px' : '24px')} 20px 20px;
  background: ${({ $bg }) => $bg};
  /* PriceCaption/PriceValue (shared with the desktop grid, where the
     equivalent PriceCell sets this same color) have no color of their own —
     they inherit it from here. */
  color: ${({ $fg }) => $fg};
`;

const MobileTitle = styled.div<{ $fg: string }>`
  font-size: 22px;
  font-weight: 900;
  line-height: 1.2;
  color: ${({ $fg }) => $fg};
`;

const MobileAudience = styled.div<{ $fg: string }>`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $fg }) => $fg};
  margin-top: 6px;
`;

/** `column.price` is shared with the desktop grid, which positions it via
 *  flex `justify-content: flex-end` instead of a margin — this is the
 *  equivalent gap for the mobile card's plain block layout. */
const MobilePriceSlot = styled.div`
  margin-top: 18px;
`;

const MobileCtaWrapper = styled.div`
  margin-top: 16px;
`;

const MobileBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobileRowBlock = styled.div<{ $withBottomBorder?: boolean }>`
  padding: 18px 20px;
  ${({ $withBottomBorder }) =>
    $withBottomBorder && `border-bottom: 1px solid ${HAIRLINE_WARM};`}
`;

const MobileRowLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${MATRIX_ROW_LABEL};
`;

const MobileRowText = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  color: ${MUTED_BODY};
  margin-top: 6px;
`;

/* ─── Premium billing toggle ──────────────────────────────────────────────── */

const PremiumPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const BillingToggle = styled.div`
  display: flex;
  gap: 0;
  background-color: ${theme.colors.grey};
  border-radius: 50px;
  padding: 4px;
  width: fit-content;
`;

const BillingOption = styled.button<{ $isActive: boolean }>`
  padding: 7px 14px;
  border: none;
  border-radius: 50px;
  background-color: ${({ $isActive }) =>
    $isActive ? theme.colors.white : 'transparent'};
  color: ${({ $isActive }) => ($isActive ? MATRIX_INK : theme.colors.greyDark)};
  font-weight: ${({ $isActive }) => ($isActive ? 'bold' : '500')};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${({ $isActive }) =>
    $isActive ? '0px 2px 8px rgba(0, 0, 0, 0.15)' : 'none'};
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
`;

const SaveBadge = styled.span`
  background-color: ${theme.colors.green};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
`;

const PremiumPriceValue = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: ${MATRIX_INK};
`;

const YearlySavingsText = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.colors.green};
`;

/* ─── Auth guard modal ────────────────────────────────────────────────────── */

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0;
`;

const IconContainer = styled.div`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
`;

const ModalMessage = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 30px;
  color: #666;
  text-align: center;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

export default PlanOverviewSection;
